import { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { useGmailIngestionAdmin } from '@/hooks/useGmailIngestionAdmin';
import { useAutomationStatus } from '@/hooks/useAutomationStatus';
import { useRealTimeActivity } from '@/hooks/useRealTimeActivity';
import { usePerformanceMonitoring } from '@/hooks/usePerformanceMonitoring';
import { MonitoringFallbackUI } from './MonitoringFallbackUI';
import { Activity, TrendingUp, Clock, Zap, AlertCircle, CheckCircle2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

export function EnhancedProcessingMetrics() {
  const { events, state } = useGmailIngestionAdmin();
  const { gmailAutomation } = useAutomationStatus();
  const { stats } = useRealTimeActivity();
  const { metrics, isConnected, lastUpdate, connectionStatus, toggleMonitoring } = usePerformanceMonitoring();

  const processedMetrics = useMemo(() => {
    const last24Hours = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const last7Days = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    
    const recentEvents = events.filter(e => new Date(e.created_at) > last24Hours);
    const weekEvents = events.filter(e => new Date(e.created_at) > last7Days);
    
    // Calculate hourly processing data for the last 24 hours
    const hourlyData = [];
    for (let i = 23; i >= 0; i--) {
      const hour = new Date(Date.now() - i * 60 * 60 * 1000);
      const hourStart = new Date(hour.getFullYear(), hour.getMonth(), hour.getDate(), hour.getHours());
      const hourEnd = new Date(hourStart.getTime() + 60 * 60 * 1000);
      
      const hourEvents = recentEvents.filter(e => {
        const eventTime = new Date(e.created_at);
        return eventTime >= hourStart && eventTime < hourEnd;
      });
      
      hourlyData.push({
        hour: hour.getHours(),
        total: hourEvents.length,
        success: hourEvents.filter(e => e.status === 'success').length,
        errors: hourEvents.filter(e => e.status === 'error').length,
        time: hourStart.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
      });
    }

    // Calculate daily data for the last 7 days
    const dailyData = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date(Date.now() - i * 24 * 60 * 60 * 1000);
      const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
      
      const dayEvents = weekEvents.filter(e => {
        const eventTime = new Date(e.created_at);
        return eventTime >= dayStart && eventTime < dayEnd;
      });
      
      dailyData.push({
        day: date.toLocaleDateString('en-US', { weekday: 'short' }),
        total: dayEvents.length,
        success: dayEvents.filter(e => e.status === 'success').length,
        errors: dayEvents.filter(e => e.status === 'error').length,
        date: dayStart
      });
    }

    // Performance metrics from real-time monitoring
    const performanceMetrics = metrics.slice(0, 20).map(metric => ({
      name: metric.metric_name,
      value: metric.value,
      timestamp: new Date(metric.created_at),
      metadata: metric.metadata
    }));

    const avgProcessingTime = performanceMetrics
      .filter(m => m.name === 'processing_time_ms')
      .reduce((acc, m) => acc + m.value, 0) / Math.max(1, performanceMetrics.filter(m => m.name === 'processing_time_ms').length);

    const throughputPerHour = recentEvents.length;
    const errorRate = recentEvents.length > 0 ? (recentEvents.filter(e => e.status === 'error').length / recentEvents.length) * 100 : 0;

    return {
      hourlyData,
      dailyData,
      performanceMetrics,
      avgProcessingTime: avgProcessingTime || 0,
      throughputPerHour,
      errorRate,
      totalProcessed: events.length,
      uptime: isConnected ? 100 : 0
    };
  }, [events, metrics, isConnected]);

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'paused': return 'secondary';
      case 'error': return 'destructive';
      default: return 'outline';
    }
  };

  return (
    <>
      <MonitoringFallbackUI 
        connectionStatus={connectionStatus}
        onRetry={toggleMonitoring}
        title="Enhanced Processing Metrics"
        description="Real-time processing analytics and performance data"
      />
      
      <div className="space-y-6">
      {/* Key Performance Indicators */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Processing Rate</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{processedMetrics.throughputPerHour}</div>
            <p className="text-xs text-muted-foreground">emails/24h</p>
            <div className="flex items-center pt-1">
              <TrendingUp className="h-3 w-3 text-green-600 mr-1" />
              <span className="text-xs text-green-600">
                {stats.successRate.toFixed(1)}% success rate
              </span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Processing Time</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {processedMetrics.avgProcessingTime > 0 
                ? `${(processedMetrics.avgProcessingTime / 1000).toFixed(1)}s`
                : 'N/A'
              }
            </div>
            <p className="text-xs text-muted-foreground">per email</p>
            <div className="mt-2">
              <Progress 
                value={Math.min(100, (processedMetrics.avgProcessingTime / 10000) * 100)} 
                className="h-1"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">System Health</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold flex items-center gap-2">
              {processedMetrics.uptime}%
              {isConnected ? (
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              ) : (
                <AlertCircle className="h-5 w-5 text-destructive" />
              )}
            </div>
            <p className="text-xs text-muted-foreground">uptime</p>
            <Badge variant={getStatusColor(gmailAutomation?.status)} className="mt-1">
              {gmailAutomation?.status || 'Unknown'}
            </Badge>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Error Rate</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {processedMetrics.errorRate.toFixed(1)}%
            </div>
            <p className="text-xs text-muted-foreground">last 24h</p>
            <div className="mt-2">
              <Progress 
                value={processedMetrics.errorRate} 
                className="h-1"
              />
            </div>
            {stats.recentErrors > 0 && (
              <p className="text-xs text-destructive mt-1">
                {stats.recentErrors} recent errors
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Processing Charts */}
      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Hourly Processing Activity</CardTitle>
            <CardDescription>Email processing volume over the last 24 hours</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={processedMetrics.hourlyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="time" />
                <YAxis />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="total" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth={2}
                  name="Total Processed"
                />
                <Line 
                  type="monotone" 
                  dataKey="errors" 
                  stroke="hsl(var(--destructive))" 
                  strokeWidth={2}
                  name="Errors"
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Daily Processing Trends</CardTitle>
            <CardDescription>7-day processing performance overview</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={processedMetrics.dailyData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="day" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="success" stackId="a" fill="hsl(var(--primary))" name="Success" />
                <Bar dataKey="errors" stackId="a" fill="hsl(var(--destructive))" name="Errors" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* System Status Information */}
      <Card>
        <CardHeader>
          <CardTitle>System Information</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-3">
          <div>
            <div className="text-sm font-medium">Connection Status</div>
            <div className="text-2xl font-bold flex items-center gap-2">
              {state?.connected ? 'Connected' : 'Disconnected'}
              <div className={`w-2 h-2 rounded-full ${
                state?.connected ? 'bg-green-500 animate-pulse' : 'bg-destructive'
              }`} />
            </div>
            {state?.mailbox_address && (
              <p className="text-xs text-muted-foreground mt-1">
                {state.mailbox_address}
              </p>
            )}
          </div>

          <div>
            <div className="text-sm font-medium">Last Sync</div>
            <div className="text-2xl font-bold">
              {state?.last_sync_at 
                ? new Date(state.last_sync_at).toLocaleTimeString()
                : 'Never'
              }
            </div>
            {lastUpdate && (
              <p className="text-xs text-muted-foreground mt-1">
                Monitoring: {lastUpdate.toLocaleTimeString()}
              </p>
            )}
          </div>

          <div>
            <div className="text-sm font-medium">Total Processed</div>
            <div className="text-2xl font-bold">
              {processedMetrics.totalProcessed.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              All time
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
    </>
  );
}